import React, { useCallback, useEffect, useState } from "react";
import { Plus, Search, Lock, Pencil, Trash2, X, Copy } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/contexts";
import { canEdit, timeLeft, fmtDate, canWrite } from "../lib/permissions";
import { Button } from "../components/ui/button";
import ExportMenu from "../components/ExportMenu";
import DuplicatesDialog from "../components/DuplicatesDialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import ContactForm from "./contacts/ContactForm";
import ContactDetail from "./contacts/ContactDetail";

const TAGS = ["Lead", "Active Buyer", "Distributor", "Manufacturer"];

export default function Contacts() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [fields, setFields] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [tag, setTag] = useState("");
  const [staff, setStaff] = useState("");
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [detailContact, setDetailContact] = useState(null);
  const [expandedProducts, setExpandedProducts] = useState({}); // per-row expand toggle
  const [productFilter, setProductFilter] = useState("");
  const [codeFilter, setCodeFilter] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importMode, setImportMode] = useState("skip"); // "skip" | "upsert" | "add"
  const [productsMaster, setProductsMaster] = useState([]);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupCount, setDupCount] = useState(0);

  const load = useCallback(async () => {
    const params = {};
    if (search) params.search = search;
    if (tag) params.tag = tag;
    if (staff) params.created_by = staff;
    if (productFilter) params.product = productFilter;
    if (codeFilter) params.product_code = codeFilter;
    const [c, p] = await Promise.all([
      api.get("/contacts", { params }),
      api.get("/products"),
    ]);
    // Guard against the same record arriving twice so a row can never render twice.
    setContacts([...new Map((c.data || []).map((x) => [x.id, x])).values()]);
    setProducts(p.data); setProductsMaster(p.data);
    if (user?.role === "manager") {
      api.get("/contacts/duplicates")
        .then((r) => setDupCount((r.data || []).reduce((a, g) => a + (g.count - 1), 0)))
        .catch(() => {});
    }
  }, [search, tag, staff, productFilter, codeFilter, user?.role]);
  useEffect(() => {
    api.get("/fields", { params: { entity: "contact" } }).then((r) => setFields(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (c) => {
    if (!confirm(`Delete contact "${c.company_name}"? All related deals will also be removed.`)) return;
    try { await api.delete(`/contacts/${c.id}`); toast.success("Contact deleted"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Contacts</h1>
          <p className="text-sm text-muted-foreground">{contacts.length} companies & buyers</p>
        </div>
        <div className="flex gap-2">
          {user?.role === "manager" && (
            <Button data-testid="import-contacts-btn" variant="outline" onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); }}>
              <Plus className="w-4 h-4 mr-2" />Import CSV
            </Button>
          )}
          {user?.role === "manager" && (
            <Button variant="outline" data-testid="open-duplicates" onClick={() => setDupOpen(true)}
              className={dupCount > 0 ? "border-amber-400 text-amber-700 hover:bg-amber-50" : ""}>
              <Copy className="w-4 h-4 mr-2" />
              {dupCount > 0 ? `${dupCount} duplicate${dupCount === 1 ? "" : "s"}` : "Duplicates"}
            </Button>
          )}
          {(user?.role === "manager" || user?.role === "unit_head") && (
            <ExportMenu resource="contacts" params={{
              search, tag, created_by: staff, product: productFilter, product_code: codeFilter,
            }} />
          )}
          {canWrite(user) && (
            <Button data-testid="new-contact-btn" className="bg-primary hover:bg-primary/90 text-white" onClick={() => setDialog({ open: true, initial: null })}>
              <Plus className="w-4 h-4 mr-2" />New Contact
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-md border border-border p-3 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="contact-search" placeholder="Search company, person, email…" value={search}
            onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <select data-testid="tag-filter" value={tag} onChange={(e) => setTag(e.target.value)} className="h-10 rounded-md border border-input bg-white px-3 text-sm">
          <option value="">All tags</option>
          {TAGS.map((t) => <option key={t}>{t}</option>)}
        </select>
        <select data-testid="staff-filter" value={staff} onChange={(e) => setStaff(e.target.value)} className="h-10 rounded-md border border-input bg-white px-3 text-sm">
          <option value="">All staff</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select data-testid="product-filter" value={productFilter} onChange={(e) => setProductFilter(e.target.value)}
          className="h-10 rounded-md border border-input bg-white px-3 text-sm">
          <option value="">All products</option>
          {productsMaster.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
        </select>
        <Input data-testid="code-filter" placeholder="Filter by code…"
          value={codeFilter} onChange={(e) => setCodeFilter(e.target.value)}
          className="h-10 w-40" />
        {(search || tag || staff || productFilter || codeFilter) && (
          <button onClick={() => { setSearch(""); setTag(""); setStaff(""); setProductFilter(""); setCodeFilter(""); }} className="text-xs text-muted-foreground hover:text-neutral-800 flex items-center gap-1"><X className="w-3 h-3" />Clear</button>
        )}
      </div>

      <div className="bg-white rounded-md border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-[11px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3">Company</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">Product / Code</th>
                <th className="text-left px-4 py-3">Volume</th>
                <th className="text-left px-4 py-3">Tags</th>
                <th className="text-left px-4 py-3">Created By</th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="contacts-table">
              {contacts.map((c) => {
                const editable = canEdit(user, c);
                const tl = timeLeft(c);
                return (
                  <tr key={c.id}
                    data-testid={`contact-row-${c.id}`}
                    onClick={() => setDetailContact(c)}
                    className="border-t border-border hover:bg-neutral-50 cursor-pointer">
                    <td className="px-4 py-3">
                      <div className="font-semibold">{c.company_name}</div>
                      <div className="text-xs text-muted-foreground">{c.designation}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div>{[c.title, c.contact_person].filter(Boolean).join(" ") || "—"}</div>
                      <div className="text-xs text-muted-foreground">{c.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const rows = Array.isArray(c.products) && c.products.length ? c.products : (c.product_name || c.product_code ? [{ product_name: c.product_name, product_code: c.product_code, volume: c.volume }] : []);
                        if (rows.length === 0) return <span>—</span>;
                        const first = rows[0];
                        const extra = rows.slice(1);
                        const isOpen = !!expandedProducts[c.id];
                        return (
                          <div>
                            <div className="flex items-center gap-1.5">
                              <span>{first.product_name || "—"}</span>
                              {extra.length > 0 && (
                                <button type="button"
                                  data-testid={`toggle-products-${c.id}`}
                                  onClick={(e) => { e.stopPropagation(); setExpandedProducts({ ...expandedProducts, [c.id]: !isOpen }); }}
                                  className="text-[10px] font-semibold text-primary bg-primary/10 hover:bg-primary/20 px-1.5 py-0.5 rounded transition-colors">
                                  {isOpen ? "− hide" : `+${extra.length} more`}
                                </button>
                              )}
                            </div>
                            {first.product_code && <div className="text-xs text-muted-foreground font-mono">{first.product_code}</div>}
                            {isOpen && extra.length > 0 && (
                              <div className="mt-2 pl-2 border-l-2 border-primary/20 space-y-1.5">
                                {extra.map((p) => (
                                  <div key={`${p.product_name || ""}-${p.product_code || ""}-${p.volume || ""}`} className="text-sm">
                                    <div>{p.product_name || "—"}</div>
                                    {p.product_code && <div className="text-xs text-muted-foreground font-mono">{p.product_code}</div>}
                                    {p.volume && <div className="text-[11px] text-neutral-600">Vol: {p.volume}</div>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {(() => {
                        const rows = Array.isArray(c.products) && c.products.length ? c.products : [];
                        if (rows.length === 0) return c.volume || "—";
                        const isOpen = !!expandedProducts[c.id];
                        const first = rows[0]?.volume || "—";
                        const others = rows.slice(1).map((p) => ({
                          key: `${p.product_name || ""}-${p.product_code || ""}`,
                          volume: p.volume || "—",
                        }));
                        return (
                          <>
                            <div>{first}</div>
                            {isOpen && others.map((o) => (
                              <div key={o.key} className="mt-2 pl-2 border-l-2 border-primary/20">{o.volume}</div>
                            ))}
                          </>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(c.tags || []).map((t) => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">{c.created_by_name}</td>
                    <td className="px-4 py-3 text-xs">
                      <div>{fmtDate(c.created_at)}</div>
                      {tl && user.id === c.created_by && <div className="text-[10px] text-orange-700 mt-0.5">{tl}</div>}
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {editable ? (
                          <Button data-testid={`edit-contact-${c.id}`} size="sm" variant="ghost" onClick={() => setDialog({ open: true, initial: c })}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                        ) : (
                          <span title="Locked (24h edit window expired or created by another staff)"><Lock className="w-3.5 h-3.5 text-neutral-400" /></span>
                        )}
                        {user.role === "manager" && (
                          <Button data-testid={`delete-contact-${c.id}`} size="sm" variant="ghost" onClick={() => remove(c)} className="text-destructive">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {contacts.length === 0 && (
                <tr><td colSpan={8} className="text-center py-12 text-muted-foreground text-sm">No contacts found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ContactForm open={dialog.open} onClose={() => setDialog({ open: false, initial: null })}
        initial={dialog.initial} onSave={load} fields={fields} products={products}
        users={users} currentUser={user} />

      <ContactDetail
        contact={detailContact}
        onClose={() => setDetailContact(null)}
        onEdit={(c) => setDialog({ open: true, initial: c })}
        fields={fields}
        canEditContact={detailContact ? canEdit(user, detailContact) : false} />

      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setImportResult(null); setImportFile(null); setImportMode("skip"); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Import Contacts from CSV</DialogTitle></DialogHeader>
          {!importResult ? (
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!importFile) { toast.error("Please choose a CSV file"); return; }
              setImportBusy(true);
              try {
                const fd = new FormData();
                fd.append("file", importFile);
                fd.append("mode", importMode);
                const r = await api.post("/contacts/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
                setImportResult(r.data);
                if ((r.data.created || 0) + (r.data.updated || 0) > 0) load();
              } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
              finally { setImportBusy(false); }
            }} className="space-y-4 text-sm">
              <div className="bg-secondary/50 border border-border rounded-md p-3 text-xs text-muted-foreground space-y-1">
                <div className="font-semibold text-neutral-800">Expected columns (header row):</div>
                <code className="text-[11px] block break-all">Company Name, Title, Contact Person, Designation, Email, Phone, Website, Address, City, State, Zip Code, Product Name, Product Code, Volume, Tags, Remarks</code>
                <div className="mt-2">• Only <b>Company Name</b> is required per row · Tags = comma-separated (defaults to "Lead" for new contacts)</div>
                <div>• Duplicate matching uses <b>Email</b> first, else <b>Company Name + Contact Person</b> (case-insensitive)</div>
              </div>
              <div>
                <Label className="mb-1 block">Duplicate handling</Label>
                <div className="space-y-2">
                  <label className="flex items-start gap-2 p-2 border border-border rounded-md cursor-pointer hover:bg-secondary/40">
                    <input type="radio" name="import-mode" value="skip" checked={importMode === "skip"}
                      onChange={() => setImportMode("skip")} data-testid="import-mode-skip" className="mt-0.5" />
                    <div>
                      <div className="font-semibold text-neutral-800">Skip duplicates <span className="text-[10px] font-normal text-primary">(Recommended)</span></div>
                      <div className="text-[11px] text-muted-foreground">Add only new contacts. Existing matches are left untouched.</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 p-2 border border-border rounded-md cursor-pointer hover:bg-secondary/40">
                    <input type="radio" name="import-mode" value="upsert" checked={importMode === "upsert"}
                      onChange={() => setImportMode("upsert")} data-testid="import-mode-upsert" className="mt-0.5" />
                    <div>
                      <div className="font-semibold text-neutral-800">Update existing + add new</div>
                      <div className="text-[11px] text-muted-foreground">Merge non-blank CSV fields into matching contacts; append new products &amp; tags. Add rows that don't match.</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 p-2 border border-border rounded-md cursor-pointer hover:bg-secondary/40">
                    <input type="radio" name="import-mode" value="add" checked={importMode === "add"}
                      onChange={() => setImportMode("add")} data-testid="import-mode-add" className="mt-0.5" />
                    <div>
                      <div className="font-semibold text-neutral-800">Add all rows</div>
                      <div className="text-[11px] text-muted-foreground">Insert every row as a new contact even if a duplicate exists (original behaviour).</div>
                    </div>
                  </label>
                </div>
              </div>
              <div>
                <Label>CSV File</Label>
                <input type="file" accept=".csv,text/csv" data-testid="import-file"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  className="mt-1 block w-full text-sm file:mr-3 file:py-2 file:px-3 file:border-0 file:rounded file:bg-primary file:text-white file:cursor-pointer" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="import-submit" disabled={importBusy || !importFile}
                  className="bg-primary hover:bg-primary/90 text-white">
                  {importBusy ? "Uploading…" : "Import"}
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center bg-green-50 border border-green-200 rounded p-3">
                  <div className="text-2xl font-heading font-bold text-green-800">{importResult.created || 0}</div>
                  <div className="text-[11px] uppercase tracking-widest text-green-900">Added</div>
                </div>
                <div className="text-center bg-blue-50 border border-blue-200 rounded p-3">
                  <div className="text-2xl font-heading font-bold text-blue-800">{importResult.updated || 0}</div>
                  <div className="text-[11px] uppercase tracking-widest text-blue-900">Updated</div>
                </div>
                <div className="text-center bg-neutral-50 border border-neutral-200 rounded p-3">
                  <div className="text-2xl font-heading font-bold text-neutral-700">{importResult.skipped || 0}</div>
                  <div className="text-[11px] uppercase tracking-widest text-neutral-700">Skipped</div>
                </div>
              </div>
              <div className="text-[11px] text-muted-foreground text-center">
                Mode: <span className="font-semibold capitalize">{importResult.mode || "skip"}</span>
              </div>
              {importResult.errors?.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded p-3">
                  <div className="font-semibold text-orange-800 mb-1">{importResult.errors.length} row(s) had errors:</div>
                  <ul className="text-xs text-orange-900 space-y-1 max-h-40 overflow-y-auto">
                    {importResult.errors.map((e) => <li key={`${e.row}-${e.error}`}>Row {e.row}: {e.error}</li>)}
                  </ul>
                </div>
              )}
              <div className="flex justify-end">
                <Button onClick={() => setImportOpen(false)} className="bg-primary hover:bg-primary/90 text-white">Done</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <DuplicatesDialog resource="contacts" open={dupOpen} onClose={() => setDupOpen(false)} onChanged={load} />
    </div>
  );
}
